"use client"

import React from "react"
import { useState, useEffect } from "react"
import { Plus, Search, Edit, Trash2, Eye, MapPin, Star, CheckCircle, XCircle } from "lucide-react"
import { Box, Flex, Card, Grid, Heading, Text, Badge, Button, IconButton, TextField, Select } from "@radix-ui/themes"
import { captainAPI } from "../services/api"
import CaptainModal from "../components/CaptainModal"
import CaptainDetailsModal from "../components/CaptainDetailsModal"
import PageHeader from "../components/ui/dashboard/PageHeader"
import LoadingSpinner from "../components/ui/dashboard/LoadingSpinner"

interface Captain {
  _id: string
  name: string
  email: string
  phone: string
  profileImage: string
  isApproved: boolean
  isActive: boolean
  isOnline: boolean
  rating: number
  totalDeliveries: number
  totalEarnings: number
  completionRate: number
  vehicleType: string
  vehicleNumber: string
  drivingLicense: string
  vehicleRegistration: string
  insurance: string
  bankDetails: {
    accountNumber: string
    routingNumber: string
    accountHolderName: string
  }
  currentLocation: {
    latitude: number
    longitude: number
    address: string
  }
  workingHours: {
    [key: string]: { start: string; end: string; isWorking: boolean }
  }
  createdAt: string
}

const CaptainsPage: React.FC = () => {
  const [captains, setCaptains] = useState<Captain[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [filterApproval, setFilterApproval] = useState("all")
  const [showModal, setShowModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedCaptain, setSelectedCaptain] = useState<Captain | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchCaptains()
  }, [currentPage, searchTerm, filterStatus, filterApproval])

  const fetchCaptains = async () => {
    try {
      setLoading(true)
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        status: filterStatus === "all" ? "" : filterStatus,
        approval: filterApproval === "all" ? "" : filterApproval,
      }
      const response = await captainAPI.getCaptains(params)
      setCaptains(response.data.captains)
      setTotalPages(response.data.totalPages)
    } catch (error) {
      console.error("Error fetching captains:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateCaptain = async (captainData: any) => {
    try {
      await captainAPI.createCaptain(captainData)
      fetchCaptains()
      setShowModal(false)
    } catch (error) {
      console.error("Error creating captain:", error)
    }
  }

  const handleUpdateCaptain = async (id: string, captainData: any) => {
    try {
      await captainAPI.updateCaptain(id, captainData)
      fetchCaptains()
      setShowModal(false)
      setSelectedCaptain(null)
    } catch (error) {
      console.error("Error updating captain:", error)
    }
  }

  const handleDeleteCaptain = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this captain?")) {
      try {
        await captainAPI.deleteCaptain(id)
        fetchCaptains()
      } catch (error) {
        console.error("Error deleting captain:", error)
      }
    }
  }

  const handleApprovalToggle = async (id: string, isApproved: boolean) => {
    try {
      await captainAPI.updateCaptain(id, { isApproved: !isApproved })
      fetchCaptains()
    } catch (error) {
      console.error("Error updating captain approval:", error)
    }
  }

  const handleStatusToggle = async (id: string, isActive: boolean) => {
    try {
      await captainAPI.updateCaptain(id, { isActive: !isActive })
      fetchCaptains()
    } catch (error) {
      console.error("Error updating captain status:", error)
    }
  }

  const filteredCaptains = captains.filter((captain) => {
    const matchesSearch =
      captain.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      captain.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      captain.phone.includes(searchTerm)
    const matchesStatus =
      filterStatus === "all" ||
      !filterStatus ||
      (filterStatus === "active" && captain.isActive) ||
      (filterStatus === "inactive" && !captain.isActive) ||
      (filterStatus === "online" && captain.isOnline) ||
      (filterStatus === "offline" && !captain.isOnline)
    const matchesApproval =
      filterApproval === "all" ||
      !filterApproval ||
      (filterApproval === "approved" && captain.isApproved) ||
      (filterApproval === "pending" && !captain.isApproved)

    return matchesSearch && matchesStatus && matchesApproval
  })

  return (
    <Flex direction="column" gap="6">
      <PageHeader 
        title="Captains" 
        description="Manage delivery captains and drivers"
        action={
          <Button onClick={() => {
            setSelectedCaptain(null)
            setShowModal(true)
          }}>
            <Plus className="w-4 h-4" />
            Add Captain
          </Button>
        }
      />

      {/* Filters */}
      <Card>
        <Flex direction="column" gap="2">
          <Text size="2" weight="medium">Filters</Text>
          <Grid columns={{ initial: "1", md: "5" }} gap="3">
            <TextField.Root
              placeholder="Search captains..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            >
              <TextField.Slot>
                <Search className="w-4 h-4" />
              </TextField.Slot>
            </TextField.Root>

            <Select.Root value={filterStatus} onValueChange={setFilterStatus}>
              <Select.Trigger placeholder="All Status" />
              <Select.Content>
                <Select.Item value="all">All Status</Select.Item>
                <Select.Item value="active">Active</Select.Item>
                <Select.Item value="inactive">Inactive</Select.Item>
                <Select.Item value="online">Online</Select.Item>
                <Select.Item value="offline">Offline</Select.Item>
              </Select.Content>
            </Select.Root>

            <Select.Root value={filterApproval} onValueChange={setFilterApproval}>
              <Select.Trigger placeholder="All Approval" />
              <Select.Content>
                <Select.Item value="all">All Approval</Select.Item>
                <Select.Item value="approved">Approved</Select.Item>
                <Select.Item value="pending">Pending</Select.Item>
              </Select.Content>
            </Select.Root>

            <Button
              variant="soft"
              color="gray"
              onClick={() => {
                setSearchTerm("")
                setFilterStatus("")
                setFilterApproval("")
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Flex>
      </Card>

      {/* Captains Grid */}
      {loading ? (
        <LoadingSpinner size="3" text="Loading captains..." />
      ) : (
        <Grid columns={{ initial: "1", md: "2", lg: "3" }} gap="6">
          {filteredCaptains.map((captain) => (
            <Card key={captain._id}>
              <Flex direction="column" gap="3">
                <Flex justify="between" align="start">
                  <Flex align="center" gap="3">
                    <img
                      src={captain.profileImage || "/placeholder-avatar.jpg"}
                      alt={captain.name}
                      style={{ 
                        width: 48, 
                        height: 48, 
                        borderRadius: "50%", 
                        objectFit: "cover" 
                      }}
                    />
                    <Box>
                      <Heading size="4">{captain.name}</Heading>
                      <Text size="2" color="gray">{captain.vehicleType}</Text>
                    </Box>
                  </Flex>
                  <Flex direction="column" gap="1">
                    <Badge color={captain.isOnline ? "green" : "gray"}>
                      {captain.isOnline ? "Online" : "Offline"}
                    </Badge>
                    <Badge color={captain.isApproved ? "green" : "red"}>
                      {captain.isApproved ? "Approved" : "Pending"}
                    </Badge>
                  </Flex>
                </Flex>

                <Flex direction="column" gap="2">
                  <Text size="2" color="gray">{captain.email}</Text>
                  <Text size="2" color="gray">{captain.phone}</Text>
                  <Flex align="center" gap="1">
                    <MapPin className="w-4 h-4" />
                    <Text size="2" color="gray">
                      {captain.currentLocation?.address || "Location not available"}
                    </Text>
                  </Flex>
                  <Flex align="center" gap="1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" style={{ color: "#facc15" }} />
                    <Text size="2" color="gray">
                       {typeof captain.rating === 'object' && (captain.rating as any)?.average 
                         ? `${(captain.rating as any).average.toFixed(1)} Rating (${(captain.rating as any).count || 0} reviews)`
                         : typeof captain.rating === 'number' 
                           ? `${captain.rating.toFixed(1)} Rating`
                           : '0.0 Rating'
                       }
                    </Text>
                  </Flex>
                </Flex>

                <Grid columns="3" gap="2" style={{ textAlign: "center" }}>
                  <Box>
                    <Text size="2" weight="bold">{captain.totalDeliveries}</Text>
                    <Text size="1" color="gray">Deliveries</Text>
                  </Box>
                  <Box>
                    <Text size="2" weight="bold">SDG {captain.totalEarnings}</Text>
                    <Text size="1" color="gray">Earnings</Text>
                  </Box>
                  <Box>
                    <Text size="2" weight="bold">{captain.completionRate}%</Text>
                    <Text size="1" color="gray">Success</Text>
                  </Box>
                </Grid>

                <Flex justify="between" align="center" pt="2" style={{ borderTop: "1px solid var(--gray-5)" }}>
                  <Flex gap="2">
                    <Button
                      size="1"
                      variant="soft"
                      color={captain.isApproved ? "red" : "green"}
                      onClick={() => handleApprovalToggle(captain._id, captain.isApproved)}
                    >
                      {captain.isApproved ? (
                        <>
                          <XCircle className="w-3 h-3" />
                          Revoke
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-3 h-3" />
                          Approve
                        </>
                      )}
                    </Button>

                    <Button
                      size="1"
                      variant="soft"
                      color={captain.isActive ? "red" : "green"}
                      onClick={() => handleStatusToggle(captain._id, captain.isActive)}
                    >
                      {captain.isActive ? "Deactivate" : "Activate"}
                    </Button>
                  </Flex>

                  <Flex gap="2">
                    <IconButton
                      size="2"
                      variant="soft"
                      color="blue"
                      onClick={() => {
                        setSelectedCaptain(captain)
                        setShowDetailsModal(true)
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </IconButton>
                    <IconButton
                      size="2"
                      variant="soft"
                      color="green"
                      onClick={() => {
                        setSelectedCaptain(captain)
                        setShowModal(true)
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </IconButton>
                    <IconButton
                      size="2"
                      variant="soft"
                      color="red"
                      onClick={() => handleDeleteCaptain(captain._id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </IconButton>
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex justify="center">
          <Flex gap="2">
            <Button
              variant="soft"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Button
                key={page}
                variant={currentPage === page ? "solid" : "soft"}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </Button>
            ))}

            <Button
              variant="soft"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      )}

      {/* Modals */}
      {showModal && (
        <CaptainModal
          captain={selectedCaptain}
          onClose={() => {
            setShowModal(false)
            setSelectedCaptain(null)
          }}
          onSave={selectedCaptain ? (data) => handleUpdateCaptain(selectedCaptain._id, data) : handleCreateCaptain}
        />
      )}

      {showDetailsModal && selectedCaptain && (
        <CaptainDetailsModal
          captain={selectedCaptain}
          onClose={() => {
            setShowDetailsModal(false)
            setSelectedCaptain(null)
          }}
        />
      )}
    </Flex>
  )
}

export default CaptainsPage
